import React, { useState } from 'react';
import { useFeathers } from '../../app/util';
import { Application } from '@feathersjs/feathers';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import CloseIcon from '@mui/icons-material/Close';
import styled from 'styled-components';
import EditIcon from '@mui/icons-material/Edit';

const { languageConfig } = require('../../language.config');

interface LanguageConfig {
  code: string;
  name: string;
}

interface TranslationEdit {
  service: string;
  objectId: string;
  textEn: string;
  textTranslated: string;
  textEdited: string;
  approved: boolean;
  editsMade: boolean;
}

interface TranslationPairEditorProps {
  serviceName: string;
  objectId: string;
  buttonComponent?: React.ComponentType<{ onClick: () => void }>;
}

const EditTranslateIcon = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  .edit-icon {
    position: absolute;
    bottom: -4px;
    right: -4px;
    font-size: 14px;
    background: white;
    border-radius: 50%;
    padding: 0px;
  }
`;

const TranslationPairEditor: React.FC<TranslationPairEditorProps> = ({
  serviceName,
  objectId,
  buttonComponent: ButtonComponent,
}) => {
  const feathers = useFeathers<Application<ServiceTypes>>();
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState('');
  const [translationEdits, setTranslationEdits] = useState<TranslationEdit[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const transformToTranslationEdits = (pairs: string[][]): TranslationEdit[] => {
    return pairs.map(pair => ({
      service: pair[0],
      objectId: pair[1],
      textEn: pair[3],
      textTranslated: pair[4],
      textEdited: pair[5] || pair[4],
      approved: false,
      editsMade: false,
    }));
  };

  const loadTranslationPairs = async (language: string) => {
    if (!language) return;
    setLoading(true);
    setError(null);
    try {
      const data = await feathers.getService('translation-pairs').get(objectId, {
        query: {
          language,
          service: serviceName
        }
      }) as string[][];
      setTranslationEdits(transformToTranslationEdits(data.slice(1)));
    } catch (err) {
      console.error('Error loading translation pairs:', err);
      setError('Failed to load translations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLanguageChange = async (event: SelectChangeEvent) => {
    const language = event.target.value;
    setSelectedLanguage(language);
    await loadTranslationPairs(language);
  };

  const handleTranslationChange = (index: number, value: string) => {
    const newEdits = [...translationEdits];
    newEdits[index].textEdited = value;
    newEdits[index].approved = true;
    newEdits[index].editsMade = true;
    setTranslationEdits(newEdits);
  };

  const handleApprovalChange = (index: number) => {
    const newEdits = [...translationEdits];
    newEdits[index].approved = !newEdits[index].approved;
    setTranslationEdits(newEdits);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      // Filter out unapproved translations and transform to the expected format
      const apiFormat = translationEdits
        .filter(edit => edit.approved)
        .map(edit => [
          edit.service,
          edit.objectId,
          `https://www.branch.vote/${edit.service}/${edit.objectId}`,
          edit.textEn,
          edit.textTranslated,
          edit.textEdited
        ]);

      // Add the header row
      const dataWithHeader = [
        ["Type", "id", "URL", "Phrase (en)", `Translation (${selectedLanguage})`, "Edits"],
        ...apiFormat
      ];

      await feathers.getService('translation-edits').create(dataWithHeader);
      setModalOpen(false);
    } catch (err) {
      console.error('Error saving translations:', err);
      setError('Failed to save translations. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleClick = () => setModalOpen(true);

  return (
    <>
      {ButtonComponent ? (
        <ButtonComponent onClick={handleClick} />
      ) : (
        <IconButton
          data-tip='Edit translations'
          onClick={handleClick}
          color="primary"
          size="large">
          <EditTranslateIcon>
            <TranslateIcon fontSize="small" />
            <EditIcon className="edit-icon" fontSize="small" />
          </EditTranslateIcon>
        </IconButton>
      )}
      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Edit Translations
            </Typography>
            <IconButton onClick={() => setModalOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box mb={3} mt={1} sx={{ maxWidth: '200px' }}>
            <FormControl fullWidth>
              <InputLabel id="language-select-label">Select Language</InputLabel>
              <Select
                labelId="language-select-label"
                value={selectedLanguage}
                label="Select Language"
                onChange={handleLanguageChange}
              >
                {languageConfig.map(({ code, name }: LanguageConfig) => (
                  <MenuItem key={code} value={code}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {selectedLanguage && (
            <Box>
              {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : error ? (
                <Typography color="error">{error}</Typography>
              ) : (
                <TableContainer component={Paper}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell width="30%">English Text</TableCell>
                        <TableCell width="30%">Translation</TableCell>
                        <TableCell width="5%">Approved</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {translationEdits.map((edit, index) => (
                        <TableRow key={index}>
                          <TableCell width="30%">{edit.textEn}</TableCell>
                          <TableCell width="30%">
                            <TextField
                              fullWidth
                              multiline
                              size="small"
                              rows={2}
                              variant="outlined"
                              value={edit.textEdited}
                              onChange={(e) => handleTranslationChange(index, e.target.value)}
                              placeholder="Enter translation..."
                            />
                            {edit.editsMade && (
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                Edits made
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell width="5%">
                            <Checkbox
                              checked={edit.approved}
                              onChange={() => handleApprovalChange(index)}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </DialogContent>
        {selectedLanguage && !loading && !error && (
          <DialogActions>
            <Button
              onClick={handleSave}
              color="primary"
              variant="contained"
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Translations'}
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </>
  );
};

export { TranslationPairEditor }; 